import { FC, ReactNode, Ref, useId } from "react"

import { ReactComponent as IconClose } from "@common_assets/svg/cross.svg"
import { ReactComponent as IconTrash } from "@common_assets/svg/trash.svg"
import {
  Button,
  ButtonProps,
  CircularProgress,
  Dialog as DialogMui,
  DialogProps,
  Fab,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
} from "@mui/material"
import IconWrapper from "components/common/icon/iconWrapper"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useTranslation } from "react-i18next"

export type DialogMaxWidth =
  | "344px"
  | "440px"
  | "534px"
  | "720px"
  | "867px"
  | "1040px"

interface IProps extends DialogProps {
  children?: ReactNode
  tabletMaxWidth?: DialogMaxWidth
  desktopMaxWidth?: DialogMaxWidth
  titleText?: string
  descriptionText?: string
  descriptionTextProps?: TypographyProps
  titleBeforeSlot?: ReactNode
  actionAcceptText?: string
  isLoading?: boolean
  actionAcceptButtonProps?: ButtonProps
  onDeleteClick?: () => void
  onActionButtonClick?: () => void
  customActionButtonsSlot?: ReactNode
  contentWrapperProps?: StackProps
  onIconClose?: boolean
  onCloseIconClick?: () => void
  titleRef?: Ref<HTMLElement>
}

const Dialog: FC<IProps> = ({
  children,
  scroll = "body",
  tabletMaxWidth = "534px",
  desktopMaxWidth = "534px",
  titleText,
  descriptionText,
  descriptionTextProps,
  titleBeforeSlot,
  actionAcceptText,
  isLoading = false,
  actionAcceptButtonProps,
  onDeleteClick,
  onActionButtonClick,
  customActionButtonsSlot,
  contentWrapperProps,
  onIconClose = true,
  onCloseIconClick,
  titleRef,
  sx,
  ...props
}) => {
  const { t } = useTranslation()
  const isTablet = useIsBreakpointUp("tablet")
  const titleId = useId()
  const descriptionId = useId()

  return (
    <DialogMui
      aria-labelledby={titleText ? titleId : undefined}
      aria-describedby={descriptionText ? descriptionId : undefined}
      {...props}
      scroll={scroll}
      fullWidth
      sx={[
        (theme) => ({
          "& .MuiDialog-paper": {
            [theme.breakpoints.up("tablet")]: {
              maxWidth: tabletMaxWidth,
            },
            [theme.breakpoints.up("desktop")]: {
              maxWidth: desktopMaxWidth,
            },
          },
        }),
        ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
      ]}
    >
      <Stack position="relative" height="100%">
        <Stack
          position="absolute"
          top="12px"
          right="12px"
          direction="row"
          gap="12px"
        >
          {onDeleteClick && (
            <Fab size="small" aria-label={t("delete")} onClick={onDeleteClick}>
              <IconWrapper color="error.main">
                <IconTrash />
              </IconWrapper>
            </Fab>
          )}
          {onIconClose && (
            <Fab
              size="small"
              aria-label={t("close")}
              onClick={() =>
                onCloseIconClick
                  ? onCloseIconClick()
                  : props.onClose && props.onClose({}, "backdropClick")
              }
            >
              <IconWrapper>
                <IconClose />
              </IconWrapper>
            </Fab>
          )}
        </Stack>
        <Stack p="32px" pb="24px" gap="24px" {...contentWrapperProps}>
          {titleText && (
            <Stack direction="row" gap="12px" alignItems="center">
              {titleBeforeSlot}
              <Typography
                ref={titleRef}
                tabIndex={titleRef ? -1 : undefined}
                id={titleId}
                variant="h3"
                color="mockup.darkBlue"
                sx={{ overflowWrap: "anywhere" }}
              >
                {titleText}
              </Typography>
            </Stack>
          )}
          {descriptionText && (
            <Typography
              variant="subtitle2"
              fontWeight={500}
              color="mockup.neutral10"
              {...descriptionTextProps}
              id={descriptionId}
            >
              {descriptionText}
            </Typography>
          )}
          {children}
        </Stack>
        {customActionButtonsSlot}
        {customActionButtonsSlot !== null && (
          <Stack
            px="32px"
            py="16px"
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.mockup.primary95}`,
            })}
            direction="row"
            gap="10px"
            justifyContent="end"
          >
            <Button
              size="small"
              fullWidth={!isTablet}
              onClick={onActionButtonClick}
              {...actionAcceptButtonProps}
            >
              <Stack
                color="common.white"
                gap="4px"
                direction="row"
                alignItems="center"
              >
                <span>
                  {isLoading && (
                    <CircularProgress size="20px" color="inherit" />
                  )}
                </span>
                {actionAcceptText || t("submit")}
              </Stack>
            </Button>
          </Stack>
        )}
      </Stack>
    </DialogMui>
  )
}

export default Dialog
